/**
 * CustomProductsAutocomplete - Service/Product selector
 *
 * Uses centralized styling from styles/utilities/view-agreement.ts
 * for consistent field heights and appearance.
 */

import { Autocomplete, Box, CircularProgress, Popper, TextField, Typography, useTheme } from '@mui/material'
import { styled, SxProps } from '@mui/material/styles'
import React from 'react'
import { Product } from 'types'
import { productAutocompleteSx } from 'styles/utilities/view-agreement'

const CustomPopper = styled(Popper)({
  width: '100% !important',
  minWidth: '200px',
  maxWidth: '445px',
})

interface CustomProductsProps {
  products: Product[]
  value: Product
  onChange: (newValue: any) => void
  label: string
  error: boolean
  isLoading: boolean
  disabled?: boolean
  sx?: SxProps
  getOptionDisabled?: (option: any) => boolean
  getOptionArchived?: (option: any) => boolean
}

const CustomProductsAutocomplete: React.FC<CustomProductsProps> = ({
  products,
  value,
  onChange,
  label,
  error,
  isLoading,
  getOptionDisabled,
  getOptionArchived,
  disabled,
  sx,
}) => {
  const theme = useTheme()
  const handleOnChange = (_event: any, newValue: any) => {
    onChange(newValue)
  }

  // Get centralized styles and merge with any custom sx
  const baseStyles = productAutocompleteSx(theme)
  const mergedStyles = sx ? { ...baseStyles, ...(sx as object) } : baseStyles

  return (
    <Box sx={{ width: '100%' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Autocomplete
          PopperComponent={CustomPopper}
          value={value || null}
          options={products}
          getOptionLabel={option => option.title || ''}
          onChange={handleOnChange}
          renderOption={(props, option) => {
            const isDisabled = !!getOptionDisabled?.(option) || false
            const isArchived = !!getOptionArchived?.(option) || false

            return (
              <li
                {...props}
                key={option.products_uuid || option.id}
                aria-disabled={isDisabled || undefined}
                style={{
                  ...(props as any).style,
                  opacity: isDisabled ? 0.45 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto',
                  padding: '6px 12px',
                }}
              >
                <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
                  <Typography noWrap sx={{ fontSize: 13 }}>
                    {option.title}
                  </Typography>
                  {isDisabled && (
                    <Typography sx={{ color: 'text.disabled', fontSize: 12 }} noWrap>
                      Selected
                    </Typography>
                  )}
                  {isArchived && (
                    <Typography sx={{ color: 'icon.nco', fontSize: 12 }} noWrap>
                      Archived
                    </Typography>
                  )}
                </Box>
              </li>
            )
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              error={error}
            />
          )}
          isOptionEqualToValue={(option, val) => option.uuid === (val ? val.uuid : undefined)}
          fullWidth
          disabled={disabled}
          sx={mergedStyles}
        />
      )}
    </Box>
  )
}

export default CustomProductsAutocomplete
